import {
  CircleNotchIcon,
  DownloadSimpleIcon,
  EnvelopeSimpleIcon,
  FileTextIcon,
} from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";

import type { DocumentAction, UserDocument } from "../types/document";
import { DocumentActionsMenu } from "./document-actions-menu";

type DocumentCardProps = {
  document: UserDocument;
  onOpen: (document: UserDocument) => void;
  onAction: (document: UserDocument, action: DocumentAction) => void;
  onThumbnailError?: () => void;
  showDownloadCta?: boolean;
  isDownloading?: boolean;
  className?: string;
};

const formatUpdatedDate = (date: string) => {
  const updatedDate = new Date(date);

  if (Number.isNaN(updatedDate.getTime())) {
    return "Date inconnue";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(updatedDate);
};

export const DocumentCard = ({
  document,
  onOpen,
  onAction,
  onThumbnailError,
  showDownloadCta = false,
  isDownloading = false,
  className,
}: DocumentCardProps) => {
  const isResume = document.kind === "resume";

  const EmptyPreviewIcon = isResume ? FileTextIcon : EnvelopeSimpleIcon;

  return (
    <article
      className={cn(
        "group overflow-hidden rounded-2xl",
        "border border-slate-200 bg-white",
        "shadow-[0_8px_24px_rgba(15,23,42,0.06)]",
        "transition-all duration-300",
        "hover:-translate-y-0.5",
        "hover:border-indigo-200",
        "hover:shadow-[0_14px_32px_rgba(15,23,42,0.10)]",
        className,
      )}
    >
      <button
        type="button"
        onClick={() => {
          onOpen(document);
        }}
        aria-label={`Ouvrir ${document.title}`}
        className={cn(
          "relative block w-full overflow-hidden",
          "bg-slate-50 text-left",
          "focus-visible:outline-none focus-visible:ring-2",
          "focus-visible:ring-inset focus-visible:ring-indigo-500",
        )}
      >
        <div className="aspect-[4/5] w-full overflow-hidden">
          {document.thumbnailUrl ? (
            <img
              src={document.thumbnailUrl}
              alt={`Aperçu de ${document.title}`}
              loading="lazy"
              onError={() => {
                onThumbnailError?.();
              }}
              className={cn(
                "size-full object-cover object-top",
                "transition-transform duration-500",
                "group-hover:scale-[1.015]",
              )}
            />
          ) : (
            <div className="flex size-full flex-col items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-emerald-50 text-indigo-600">
              <span className="grid size-16 place-items-center rounded-2xl bg-white shadow-sm">
                <EmptyPreviewIcon aria-hidden size={32} weight="duotone" />
              </span>

              <span className="mt-3 text-xs font-medium text-slate-500">Aperçu indisponible</span>
            </div>
          )}
        </div>
      </button>

      <div className="flex min-h-[62px] items-center gap-2 border-t border-slate-200 px-3 py-2.5 sm:gap-3 sm:px-3.5">
        <button
          type="button"
          onClick={() => {
            onOpen(document);
          }}
          className={cn(
            "min-w-0 flex-1 rounded-lg text-left",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-indigo-500",
          )}
        >
          <span className="block truncate text-[13px] font-bold text-indigo-950">
            {document.title}
          </span>

          <span className="mt-0.5 block truncate text-[10px] text-slate-500 sm:text-[11px]">
            Modifié le {formatUpdatedDate(document.updatedAt)}
          </span>
        </button>

        <div className="flex shrink-0 items-center gap-1.5">
          {showDownloadCta && (
            <Button
              size="sm"
              disabled={isDownloading}
              title="Télécharger ce document"
              onClick={(event) => {
                event.stopPropagation();

                onAction(document, "download");
              }}
              className="h-8 rounded-lg bg-emerald-500 px-2.5 py-0 text-xs font-semibold text-white shadow-sm transition-transform hover:scale-105 hover:bg-emerald-600 disabled:opacity-70 disabled:hover:scale-100 sm:px-3"
            >
              {isDownloading ? (
                <CircleNotchIcon
                  aria-hidden
                  size={14}
                  weight="bold"
                  className="animate-spin sm:mr-1.5"
                />
              ) : (
                <DownloadSimpleIcon aria-hidden size={14} weight="bold" className="sm:mr-1.5" />
              )}

              <span className="hidden sm:inline">
                {isDownloading ? "Génération…" : "Télécharger"}
              </span>
            </Button>
          )}

          <DocumentActionsMenu
            documentTitle={document.title}
            onAction={(action) => {
              onAction(document, action);
            }}
          />
        </div>
      </div>
    </article>
  );
};
